/**
 * Strategy Scanner — live paper trading for S1 (Multi-Confluence) + S2 (SMC).
 * Scans every 5 min, detects signals, tracks trades in points.
 */

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = "/api/zerodha";

function Badge({ text, color }) {
  return <span style={{ display: "inline-block", padding: "1px 6px", borderRadius: 3, fontSize: 9, fontWeight: 700, background: color + "22", color, border: `1px solid ${color}` }}>{text}</span>;
}

// Map a confidence label to a colour (A+ best → C weakest).
function confColor(label) {
  return { "A+": "#34d399", A: "#60a5fa", B: "#f59e0b", C: "#f87171" }[label] || "var(--grey-text2)";
}

// Tooltip text breaking down the factor contributions behind the score.
function confTitle(t) {
  if (!Array.isArray(t.confluence) || !t.confluence.length) return "No confidence breakdown";
  return t.confluence.map(f => `${f.name}: ${f.score}/${f.max} — ${f.detail}`).join("\n");
}

// Confidence badge: "78 A" coloured by tier, hover shows the factor breakdown.
function ConfBadge({ t }) {
  if (t.confidence == null) return <span style={{ color: "var(--edge)" }}>-</span>;
  const color = confColor(t.confidence_label);
  return (
    <span title={confTitle(t)} style={{ cursor: "help", display: "inline-block", padding: "1px 6px", borderRadius: 3, fontSize: 9, fontWeight: 700, background: color + "22", color, border: `1px solid ${color}` }}>
      {t.confidence} {t.confidence_label}
    </span>
  );
}

function exportCSV(trades) {
  if (!trades.length) return;
  const h = "Strategy,Confidence,Conf Label,Direction,Index,Entry Time,Exit Time,Entry Spot,Exit Spot,SL,T1,T1 Pts,T2,T2 Pts,Risk,P&L Pts,R-Multiple,MFE,MAE,Result,Exit Reason,Pattern/Setup\n";
  const rows = trades.map(t =>
    `${t.strategy},${t.confidence??""},${t.confidence_label||""},${t.direction},${t.index},${t.entry_time},${t.exit_time||""},${t.entry_spot},${t.exit_spot||""},${t.sl_spot},${t.t1_spot},${targetPoints(t.entry_spot,t.t1_spot,t.direction)},${t.t2_spot},${targetPoints(t.entry_spot,t.t2_spot,t.direction)},${t.risk_pts},${t.pnl_pts||0},${t.r_multiple||0},${t.max_favorable_pts||0},${t.max_adverse_pts||0},${t.result||"OPEN"},${t.exit_reason||""},${t.pattern||t.setup||""}`
  ).join("\n");
  const blob = new Blob([h + rows], { type: "text/csv" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "strategy-scanner-trades.csv"; a.click();
}

export default function StrategyScanner() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const poll = async () => {
    try {
      const res = await axios.get(`${API}/strategy-scanner/status`, { timeout: 10000 });
      if (res.data.success) setStatus(res.data);
    } catch {}
  };

  useEffect(() => {
    poll();
    pollRef.current = setInterval(poll, 10000);
    return () => clearInterval(pollRef.current);
  }, []);

  const start = async () => {
    setLoading(true);
    try { await axios.post(`${API}/strategy-scanner/start`, { index: "NIFTY" }); } catch {}
    setTimeout(poll, 1000);
    setLoading(false);
  };
  const stop = async () => {
    setLoading(true);
    try { await axios.post(`${API}/strategy-scanner/stop`); } catch {}
    setTimeout(poll, 1000);
    setLoading(false);
  };
  const clear = async () => {
    if (!window.confirm("Clear all closed trades?")) return;
    try { await axios.post(`${API}/strategy-scanner/clear`); } catch {}
    setTimeout(poll, 500);
  };

  const trades = status?.trades || [];
  const openTrades = trades.filter(t => t.status === "OPEN");
  const closedTrades = trades.filter(t => t.status === "CLOSED");
  const s1 = status?.s1 || {};
  const s2 = status?.s2 || {};

  const s1Closed = closedTrades.filter(t => t.strategy === "S1");
  const s2Closed = closedTrades.filter(t => t.strategy === "S2");
  const totalPnl = round(s1.totalPnl + s2.totalPnl);

  return (
    <div className="five-ema">
      <div className="dashboard-header">
        <div>
          <h2>Strategy Scanner</h2>
          <p className="dashboard-subtitle">Live paper trading — S1 (Multi-Confluence) + S2 (SMC) | All P&L in points | Conf = 0–100 setup quality (hover for breakdown): A+ ≥80, A ≥65, B ≥50, C below</p>
        </div>
        <div className="dashboard-actions">
          <button className={`btn ${status?.active ? "btn-danger" : "btn-primary"}`}
            onClick={status?.active ? stop : start} disabled={loading}>
            {status?.active ? "Stop Scanner" : "Start Scanner"}
          </button>
        </div>
      </div>

      {/* Status bar */}
      {status && (
        <div style={{ display: "flex", gap: 16, padding: "8px 12px", background: status.active ? "var(--tint-pos)" : "var(--panel3)", borderBottom: "1px solid var(--surface3)", fontSize: 12, color: "var(--grey-text)", flexWrap: "wrap", alignItems: "center" }}>
          <span>{status.active ? <span style={{ color: "#34d399" }}>SCANNING</span> : "STOPPED"}</span>
          <span>Scans: {status.scanCount}</span>
          {status.lastScanTime && <span>Last: {new Date(status.lastScanTime).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}</span>}
          <span>Market: {status.marketOpen ? <span style={{ color: "#34d399" }}>OPEN</span> : <span style={{ color: "#f87171" }}>CLOSED</span>}</span>
          <span>Open: <strong>{status.openTrades}</strong></span>
          <span style={{ color: "var(--edge)" }}>|</span>
          <span>Combined P&L: <strong className={totalPnl >= 0 ? "positive" : "negative"}>{totalPnl > 0 ? "+" : ""}{totalPnl} pts</strong></span>
        </div>
      )}

      {/* Strategy summary cards */}
      {status && (
        <div style={{ display: "flex", gap: 12, padding: "12px", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 250, padding: 12, borderRadius: 8, background: "#1a2744", border: "1px solid #334" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#60a5fa", marginBottom: 6 }}>S1 — Multi-Confluence</div>
            <div style={{ fontSize: 11, color: "var(--grey-text)" }}>EMA 21/55 | SL 2×ATR | T1 1.5R | T2 4R</div>
            <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 12 }}>
              <span>Open: <strong>{s1.open}</strong></span>
              <span>Closed: <strong>{s1.closed}</strong></span>
              <span>Wins: <strong style={{ color: "#34d399" }}>{s1.wins}</strong></span>
              <span>WR: <strong>{s1.closed > 0 ? Math.round(s1.wins / s1.closed * 100) : 0}%</strong></span>
              <span>P&L: <strong className={s1.totalPnl >= 0 ? "positive" : "negative"}>{s1.totalPnl > 0 ? "+" : ""}{s1.totalPnl} pts</strong></span>
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 250, padding: 12, borderRadius: 8, background: "#2d1a2e", border: "1px solid #433" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#a78bfa", marginBottom: 6 }}>S2 — SMC</div>
            <div style={{ fontSize: 11, color: "var(--grey-text)" }}>Pivot 3/4 | SL 1×ATR | T1 1R | T2 2R</div>
            <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 12 }}>
              <span>Open: <strong>{s2.open}</strong></span>
              <span>Closed: <strong>{s2.closed}</strong></span>
              <span>Wins: <strong style={{ color: "#34d399" }}>{s2.wins}</strong></span>
              <span>WR: <strong>{s2.closed > 0 ? Math.round(s2.wins / s2.closed * 100) : 0}%</strong></span>
              <span>P&L: <strong className={s2.totalPnl >= 0 ? "positive" : "negative"}>{s2.totalPnl > 0 ? "+" : ""}{s2.totalPnl} pts</strong></span>
            </div>
          </div>
        </div>
      )}

      {/* Open trades */}
      {openTrades.length > 0 && (
        <div style={{ padding: "8px 12px" }}>
          <h4 style={{ margin: "0 0 6px", fontSize: 13, color: "#f59e0b" }}>Open Trades ({openTrades.length})</h4>
          <div className="stocks-table-wrapper">
            <table className="stocks-table" style={{ fontSize: 11 }}>
              <thead><tr>
                <th>Strat</th><th>Conf</th><th>Dir</th><th>Entry Time</th><th>Entry</th><th>SL</th><th>T1</th><th>T1 Pts</th><th>T2</th><th>T2 Pts</th><th>Risk</th><th>MFE</th><th>MAE</th><th>T1 Hit</th><th>Signal</th>
              </tr></thead>
              <tbody>
                {openTrades.map(t => (
                  <tr key={t.id} className="trade-open-row">
                    <td><Badge text={t.strategy} color={t.strategy === "S1" ? "#60a5fa" : "#a78bfa"} /></td>
                    <td><ConfBadge t={t} /></td>
                    <td><span className={`signal-badge ${t.direction === "BULLISH" ? "badge-long" : "badge-short"}`}>{t.direction === "BULLISH" ? "BULL" : "BEAR"}</span></td>
                    <td style={{ fontSize: 10 }}>{t.entry_time ? new Date(t.entry_time).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-"}</td>
                    <td><strong>{t.entry_spot}</strong></td>
                    <td className="sl">{t.sl_spot}</td>
                    <td>{t.t1_spot}</td>
                    <td style={{ color: "#34d399" }}>+{targetPoints(t.entry_spot, t.t1_spot, t.direction)}</td>
                    <td>{t.t2_spot}</td>
                    <td style={{ color: "#34d399" }}>+{targetPoints(t.entry_spot, t.t2_spot, t.direction)}</td>
                    <td>{t.risk_pts} pts</td>
                    <td style={{ color: "#34d399" }}>{t.max_favorable_pts}</td>
                    <td style={{ color: "#f87171" }}>{t.max_adverse_pts}</td>
                    <td>{t.t1_hit ? "YES" : "-"}</td>
                    <td style={{ fontSize: 10 }}>{t.pattern || t.setup || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Closed trades */}
      <div style={{ padding: "8px 12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h4 style={{ margin: 0, fontSize: 13, color: "var(--grey-text)" }}>Closed Trades ({closedTrades.length})</h4>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-export" onClick={() => exportCSV(trades)}>Export CSV</button>
            {closedTrades.length > 0 && <button className="btn btn-sm btn-danger" onClick={clear}>Clear</button>}
          </div>
        </div>
        {closedTrades.length > 0 && (
          <div className="stocks-table-wrapper">
            <table className="stocks-table" style={{ fontSize: 11 }}>
              <thead><tr>
                <th>Strat</th><th>Conf</th><th>Dir</th><th>Entry</th><th>Exit</th><th>Entry Spot</th><th>Exit Spot</th>
                <th>SL</th><th>Risk</th><th>T1 Pts</th><th>T2 Pts</th><th>P&L</th><th>R</th><th>MFE</th><th>MAE</th><th>Result</th><th>Exit Reason</th><th>Signal</th>
              </tr></thead>
              <tbody>
                {closedTrades.map(t => (
                  <tr key={t.id} className={t.result === "WIN" ? "trade-win" : "trade-loss"}>
                    <td><Badge text={t.strategy} color={t.strategy === "S1" ? "#60a5fa" : "#a78bfa"} /></td>
                    <td><ConfBadge t={t} /></td>
                    <td><span className={`signal-badge ${t.direction === "BULLISH" ? "badge-long" : "badge-short"}`}>{t.direction === "BULLISH" ? "BULL" : "BEAR"}</span></td>
                    <td style={{ fontSize: 10 }}>{t.entry_time ? new Date(t.entry_time).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-"}</td>
                    <td style={{ fontSize: 10 }}>{t.exit_time ? new Date(t.exit_time).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-"}</td>
                    <td>{t.entry_spot}</td>
                    <td>{t.exit_spot}</td>
                    <td className="sl">{t.sl_spot}</td>
                    <td>{t.risk_pts} pts</td>
                    <td style={{ color: "#34d399", fontSize: 10 }}>+{targetPoints(t.entry_spot, t.t1_spot, t.direction)}</td>
                    <td style={{ color: "#34d399", fontSize: 10 }}>+{targetPoints(t.entry_spot, t.t2_spot, t.direction)}</td>
                    <td className={t.pnl_pts >= 0 ? "positive" : "negative"}>{t.pnl_pts > 0 ? "+" : ""}{t.pnl_pts} pts</td>
                    <td style={{ color: t.r_multiple >= 0 ? "#34d399" : "#f87171" }}>{(t.r_multiple || 0).toFixed(1)}R</td>
                    <td style={{ color: "#34d399", fontSize: 10 }}>{t.max_favorable_pts}</td>
                    <td style={{ color: "#f87171", fontSize: 10 }}>{t.max_adverse_pts}</td>
                    <td><Badge text={t.result || "?"} color={t.result === "WIN" ? "#34d399" : "#f87171"} /></td>
                    <td style={{ fontSize: 10 }}>{t.exit_reason}</td>
                    <td style={{ fontSize: 10 }}>{t.pattern || t.setup || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!status?.active && closedTrades.length === 0 && (
        <div style={{ padding: 24, textAlign: "center", color: "var(--btn-border)" }}>
          <p>Click <strong>Start Scanner</strong> to begin paper trading with both strategies on NIFTY.</p>
          <p style={{ fontSize: 12 }}>Scans every 5 min during market hours. Trades persist across page refreshes.</p>
          <p style={{ fontSize: 11, color: "var(--edge)" }}>S1: EMA 21/55, SL 2×ATR, T1 1.5R, T2 4R | S2: Pivot 3/4, SL 1×ATR, T1 1R, T2 2R</p>
        </div>
      )}
    </div>
  );
}

function round(n) { return Math.round((n || 0) * 10) / 10; }

// Points gained if a target is reached (positive for both directions).
function targetPoints(entry, target, dir) {
  if (entry == null || target == null) return 0;
  return round(dir === "BULLISH" ? target - entry : entry - target);
}
